<template>
  <div
    v-if="resultado.puntos.length > 0"
    :class="modal ? 'h-[55vh] min-h-80' : 'h-80'"
  >
    <canvas ref="canvasRef"></canvas>
  </div>
  <div
    v-else
    class="py-10 text-center text-sm text-gray-500 dark:text-gray-400"
  >
    Sin información disponible
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { Chart } from 'chart.js/auto'
import type {
  IListaSqlResultadoGrafica,
  TipoGraficaListaSql
} from '@/modules/Configuracion/interfaces'

const props = withDefaults(defineProps<{
  resultado: IListaSqlResultadoGrafica
  tipoGrafica: TipoGraficaListaSql
  modal?: boolean
}>(), {
  modal: false
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
let grafica: Chart | null = null

const colores = [
  '#2563eb',
  '#7c3aed',
  '#0891b2',
  '#059669',
  '#d97706',
  '#dc2626',
  '#4f46e5',
  '#db2777',
  '#0d9488',
  '#65a30d'
]

const destruirGrafica = () => {
  grafica?.destroy()
  grafica = null
}

const dibujarGrafica = async () => {
  destruirGrafica()
  if (props.resultado.puntos.length === 0) return

  await nextTick()
  if (!canvasRef.value) return

  const labels = props.resultado.puntos.map(punto => punto.label)
  const valores = props.resultado.puntos.map(punto => punto.value)
  const backgroundColor = labels.map((_, indice) => colores[indice % colores.length])

  grafica = new Chart(canvasRef.value, {
    type: props.tipoGrafica,
    data: {
      labels,
      datasets: [{
        label: props.resultado.titulo,
        data: valores,
        backgroundColor,
        borderColor: props.tipoGrafica === 'bar' ? backgroundColor : '#ffffff',
        borderWidth: props.tipoGrafica === 'bar' ? 0 : 2,
        borderRadius: props.tipoGrafica === 'bar' ? 6 : 0,
        maxBarThickness: props.tipoGrafica === 'bar' ? 64 : undefined,
        hoverOffset: props.tipoGrafica === 'doughnut' ? 6 : undefined
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: props.tipoGrafica === 'doughnut' ? '62%' : undefined,
      plugins: {
        legend: {
          display: props.tipoGrafica === 'doughnut',
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 14
          }
        }
      },
      scales: props.tipoGrafica === 'bar'
        ? {
            y: { beginAtZero: true },
            x: { grid: { display: false } }
          }
        : undefined
    }
  })
}

watch(
  () => [props.resultado, props.tipoGrafica],
  () => { void dibujarGrafica() },
  { immediate: true, deep: true }
)

onBeforeUnmount(destruirGrafica)
</script>
